// Запросы активностей и сна: /api/activities, /api/sleep. Используют экраны «Сон», «Активности»
// и блок привязанной активности в карточке тренировки.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '@/api/client'

export type Sport = 'run' | 'bike' | 'walk' | 'swim' | 'strength' | 'cardio' | 'other'

export type LinkedSession = {
  id: string
  started_at: string
  ended_at: string | null
  template_name: string | null
}

export type Activity = {
  id: string
  garmin_activity_id: number | null
  sport: Sport | string
  garmin_type: string | null
  name: string | null
  start_at: string
  day: string
  duration_s: number | null
  distance_m: number | null
  avg_hr: number | null
  max_hr: number | null
  calories: number | null
  aerobic_te: number | null
  anaerobic_te: number | null
  training_load: number | null
  hr_zones_s: number[] | null
  has_hr_series: boolean
  workout_session_id: string | null
  link_manual: boolean
  session: LinkedSession | null
}

export type ActivityDetail = Activity & { hr_series: [number, number][] | null }

export type ActivityPage = { items: Activity[]; next_cursor: string | null }

export type Sleep = {
  id: string
  day: string
  start_at: string | null
  end_at: string | null
  duration_s: number | null
  deep_s: number | null
  light_s: number | null
  rem_s: number | null
  awake_s: number | null
  score: number | null
  source: string
}

export type ActivityQuery = {
  start?: string | null
  end?: string | null
  sport?: string | null
  session_id?: string | null
  limit?: number
  cursor?: string | null
}

export const activityKeys = {
  all: ['activities'] as const,
  list: (q: ActivityQuery) => ['activities', 'list', q] as const,
  detail: (id: string) => ['activities', 'detail', id] as const,
  sleep: (start: string | null, end: string | null) => ['sleep', start, end] as const,
}

export const activitiesApi = {
  list: (q: ActivityQuery) =>
    api.get<ActivityPage>('/activities', {
      query: { start: q.start, end: q.end, sport: q.sport, session_id: q.session_id, limit: q.limit, cursor: q.cursor },
    }),
  get: (id: string) => api.get<ActivityDetail>(`/activities/${id}`),
  link: (id: string, sessionId: string | null) =>
    api.post<Activity>(`/activities/${id}/link`, { session_id: sessionId }),
  sleep: (start: string | null, end: string | null) => api.get<Sleep[]>('/sleep', { query: { start, end } }),
}

/** Ответ списка; кривой ответ (например, заглушка в тестах) считается пустым. */
export function itemsOf(page: ActivityPage | undefined): Activity[] {
  return Array.isArray(page?.items) ? page.items : []
}

export function useActivities(q: ActivityQuery, enabled = true) {
  return useQuery({ queryKey: activityKeys.list(q), queryFn: () => activitiesApi.list(q), enabled })
}

export function useActivity(id: string) {
  return useQuery({ queryKey: activityKeys.detail(id), queryFn: () => activitiesApi.get(id), enabled: Boolean(id) })
}

export function useSleep(start: string | null, end: string | null) {
  return useQuery({ queryKey: activityKeys.sleep(start, end), queryFn: () => activitiesApi.sleep(start, end) })
}

export function useLinkActivity() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, sessionId }: { id: string; sessionId: string | null }) => activitiesApi.link(id, sessionId),
    onSuccess: () => client.invalidateQueries({ queryKey: activityKeys.all }),
  })
}

export type DaySession = { id: string; started_at: string; ended_at: string | null; template_name: string | null }

/** Тренировки дня — для ручной привязки активности. */
export function useDaySessions(day: string | null) {
  return useQuery({
    queryKey: ['training', 'sessions', 'day', day],
    queryFn: () =>
      api.get<{ items: DaySession[]; next_cursor: string | null }>('/sessions', {
        query: { start: day, end: day, limit: 30 },
      }),
    enabled: Boolean(day),
  })
}
